import { useMemo, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeft, BarChart3, Bot, CalendarDays, Clock, Loader2, MessageSquare, Sparkles, TrendingUp,
    Users
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { useMultipleAgentTimelines } from "@/hooks/analytics";
import { useUserGenieAnalytics } from "@/hooks/analytics/useUserGenieAnalytics";
import { ApiService } from "@/services/api/ApiService";
import { AgentAnalytics } from "@/services/analytics/AnalyticsService";
import { Profile } from "@/types/auth/auth";
import ConversationTimelineChart from "@/pages/UserHub/Analytics/components/ConversationTimelineChart";
import { getRoleBadgeColor, getRoleLabel } from "../utils/userBadges";

type SortKey = "name" | "total" | "recent" | "lastUsed";
type SortDir = "asc" | "desc";

const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

const useUserProfile = (userId: string | undefined) =>
  useQuery({
    queryKey: ["profile-admin-get", userId],
    queryFn: async () => {
      const res = await ApiService.invoke<{ profile: Profile }>({
        resource: "profiles",
        action: "get",
        id: userId,
      });
      return res.profile;
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

type GenieStatus = "active" | "idle" | "dormant" | "never";

const getGenieStatus = (lastUsedAt: string | null): GenieStatus => {
  if (!lastUsedAt) return "never";
  const age = Date.now() - new Date(lastUsedAt).getTime();
  if (age <= sevenDaysMs) return "active";
  if (age <= thirtyDaysMs) return "idle";
  return "dormant";
};

const STATUS_BADGE: Record<GenieStatus, { label: string; className: string; dot: string }> = {
  active: {
    label: "Active",
    className: "bg-genie-primary/10 text-genie-primary border-genie-primary/20",
    dot: "bg-genie-primary",
  },
  idle: {
    label: "Idle",
    className: "bg-genie-accent/15 text-[#8a6d1f] border-genie-accent/30",
    dot: "bg-genie-accent",
  },
  dormant: {
    label: "Dormant",
    className: "bg-gray-100 text-gray-600 border-gray-200",
    dot: "bg-gray-400",
  },
  never: {
    label: "Never Used",
    className: "bg-gray-50 text-gray-400 border-gray-200",
    dot: "bg-gray-300",
  },
};

const formatLastUsed = (iso: string | null): string => {
  if (!iso) return "Never";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "Never";
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const sortGenies = (genies: AgentAnalytics[], key: SortKey, dir: SortDir): AgentAnalytics[] => {
  const sign = dir === "asc" ? 1 : -1;
  const copy = [...genies];
  copy.sort((a, b) => {
    switch (key) {
      case "name":
        return sign * a.name.localeCompare(b.name);
      case "total":
        return sign * (a.conversationCount - b.conversationCount);
      case "recent":
        return sign * (a.recentConversations - b.recentConversations);
      case "lastUsed": {
        const aT = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
        const bT = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
        return sign * (aT - bT);
      }
      default:
        return 0;
    }
  });
  return copy;
};

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  accent: "primary" | "secondary" | "accent";
  isLoading: boolean;
}

const accentStyles: Record<KpiCardProps["accent"], { iconBg: string; iconText: string; border: string }> = {
  primary: {
    iconBg: "bg-genie-primary/10",
    iconText: "text-genie-primary",
    border: "border-genie-primary/15",
  },
  secondary: {
    iconBg: "bg-genie-secondary/10",
    iconText: "text-genie-secondary",
    border: "border-genie-secondary/15",
  },
  accent: {
    iconBg: "bg-genie-accent/15",
    iconText: "text-[#a37a1c]",
    border: "border-genie-accent/25",
  },
};

const KpiCard = ({ icon, label, value, accent, isLoading }: KpiCardProps) => {
  const styles = accentStyles[accent];
  return (
    <div className={`bg-white p-4 md:p-5 rounded-2xl border ${styles.border} shadow-sm flex items-center gap-4`}>
      <div className={`h-11 w-11 rounded-xl ${styles.iconBg} flex items-center justify-center flex-shrink-0`}>
        <div className={`h-5 w-5 ${styles.iconText}`}>{icon}</div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-[#7C8B9C] uppercase tracking-wide leading-tight">{label}</p>
        <p className="text-2xl font-bold text-[#1A1A1A] leading-tight mt-1">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-gray-300" /> : value}
        </p>
      </div>
    </div>
  );
};

const UserAnalyticsPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data: profile, isLoading: isProfileLoading } = useUserProfile(userId);
  const { data: genies, isLoading: isGeniesLoading } = useUserGenieAnalytics(userId);

  const [sortKey, setSortKey] = useState<SortKey>("lastUsed");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const agentIds = useMemo(() => (genies || []).map((g) => g.id), [genies]);
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - thirtyDaysMs);
    return { startDate: start, endDate: end };
  }, []);
  const { data: timelines, isLoading: isTimelinesLoading } = useMultipleAgentTimelines(
    agentIds,
    startDate,
    endDate,
  );

  const kpis = useMemo(() => {
    const list = genies || [];
    const totalGenies = list.length;
    const totalConversations = list.reduce((sum, g) => sum + g.conversationCount, 0);
    const recent7d = list.reduce((sum, g) => sum + g.recentConversations, 0);

    const thirtyDaysAgo = Date.now() - thirtyDaysMs;
    const recent30d = (timelines || []).reduce((sum, t) => {
      return (
        sum +
        t.data.reduce((dSum, point) => {
          const pointTime = new Date(point.date).getTime();
          return pointTime >= thirtyDaysAgo ? dSum + point.conversations : dSum;
        }, 0)
      );
    }, 0);

    const avgPerGenie = totalGenies > 0 ? Math.round(totalConversations / totalGenies) : 0;
    return { totalGenies, totalConversations, recent7d, recent30d, avgPerGenie };
  }, [genies, timelines]);

  const sortedGenies = useMemo(
    () => sortGenies(genies || [], sortKey, sortDir),
    [genies, sortKey, sortDir],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return <span className="ml-1 text-genie-primary">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const isConsumer = profile?.role === "consumer";
  const displayName = profile?.full_name || profile?.email || "User";

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
        {/* Back link */}
        <Link
          to="/admin/users"
          className="inline-flex items-center text-sm text-[#7C8B9C] hover:text-genie-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Users
        </Link>

        {/* Header card with gradient border */}
        <div className="p-[1.5px] rounded-2xl bg-gradient-to-r from-genie-primary to-genie-secondary mb-6">
          <div className="rounded-[14px] bg-white px-5 md:px-8 py-5 md:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-genie-primary to-genie-secondary flex items-center justify-center flex-shrink-0 shadow-md shadow-genie-primary/20">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs text-[#7C8B9C] font-medium uppercase tracking-wide mb-0.5">
                    <Sparkles className="h-3 w-3 text-genie-primary" />
                    Usage Analytics
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A] truncate">
                    {isProfileLoading ? "Loading..." : displayName}
                  </h1>
                  {profile && (
                    <div className="flex items-center gap-2 mt-1 text-sm text-[#7C8B9C]">
                      <CopyableEmail email={profile.email} fallback="No email" textClassName="truncate" />
                      {profile.role && (
                        <Badge className={`${getRoleBadgeColor(profile.role)} border-0`}>
                          {getRoleLabel(profile.role)}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {isConsumer ? (
          <div className="bg-white rounded-2xl border border-[#E5EAF0] shadow-sm p-12 text-center">
            <div className="h-14 w-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
              <Users className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-[#1A1A1A] font-semibold">This user is a consumer and does not own genies.</p>
            <p className="text-[#7C8B9C] text-sm mt-1">
              Analytics are only available for owners and administrators.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-6"
              onClick={() => navigate("/admin/users")}
            >
              Back to Users
            </Button>
          </div>
        ) : (
          <>
            {/* KPI Cards — brand colors only */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4 mb-6">
              <KpiCard
                icon={<Bot className="w-full h-full" />}
                label="Total Genies"
                value={kpis.totalGenies}
                accent="primary"
                isLoading={isGeniesLoading}
              />
              <KpiCard
                icon={<MessageSquare className="w-full h-full" />}
                label="Total Conversations"
                value={kpis.totalConversations}
                accent="secondary"
                isLoading={isGeniesLoading}
              />
              <KpiCard
                icon={<TrendingUp className="w-full h-full" />}
                label="Last 7 Days"
                value={kpis.recent7d}
                accent="accent"
                isLoading={isGeniesLoading}
              />
              <KpiCard
                icon={<CalendarDays className="w-full h-full" />}
                label="Last 30 Days"
                value={kpis.recent30d}
                accent="primary"
                isLoading={isGeniesLoading || isTimelinesLoading}
              />
              <KpiCard
                icon={<BarChart3 className="w-full h-full" />}
                label="Avg / Genie"
                value={kpis.avgPerGenie}
                accent="secondary"
                isLoading={isGeniesLoading}
              />
            </div>

            {/* Timeline Chart */}
            <div className="bg-white rounded-2xl border border-[#E5EAF0] shadow-sm p-5 md:p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-[#1A1A1A]">Conversation Trends</h2>
                  <p className="text-xs text-[#7C8B9C] mt-0.5">Last 30 days of conversation activity per genie.</p>
                </div>
              </div>
              <div className="h-72 md:h-80">
                <ConversationTimelineChart
                  timelines={timelines || []}
                  isLoading={isTimelinesLoading || isGeniesLoading}
                />
              </div>
            </div>

            {/* Per-Genie Breakdown */}
            <div className="bg-white rounded-2xl border border-[#E5EAF0] shadow-sm overflow-hidden">
              <div className="p-5 md:p-6 border-b border-[#E5EAF0]">
                <h2 className="text-base md:text-lg font-semibold text-[#1A1A1A]">Genies Breakdown</h2>
                <p className="text-xs text-[#7C8B9C] mt-0.5">
                  Per-genie usage stats. Click any column header to sort.
                </p>
              </div>

              {isGeniesLoading ? (
                <div className="p-12 flex items-center justify-center gap-3 text-[#7C8B9C]">
                  <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
                  <span className="text-sm">Loading genies...</span>
                </div>
              ) : sortedGenies.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 flex items-center justify-center mx-auto mb-4">
                    <Bot className="h-7 w-7 text-genie-primary" />
                  </div>
                  <p className="text-[#1A1A1A] font-semibold">No genies yet</p>
                  <p className="text-[#7C8B9C] text-sm mt-1">
                    This user hasn't created any genies.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F9FAFC] hover:bg-[#F9FAFC]">
                        <TableHead
                          className="cursor-pointer select-none text-xs font-semibold text-[#7C8B9C] uppercase tracking-wide hover:text-genie-primary transition-colors"
                          onClick={() => handleSort("name")}
                        >
                          Genie{sortIndicator("name")}
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer select-none text-xs font-semibold text-[#7C8B9C] uppercase tracking-wide hover:text-genie-primary transition-colors"
                          onClick={() => handleSort("total")}
                        >
                          Total{sortIndicator("total")}
                        </TableHead>
                        <TableHead
                          className="text-right cursor-pointer select-none text-xs font-semibold text-[#7C8B9C] uppercase tracking-wide hover:text-genie-primary transition-colors"
                          onClick={() => handleSort("recent")}
                        >
                          7 Days{sortIndicator("recent")}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none text-xs font-semibold text-[#7C8B9C] uppercase tracking-wide hover:text-genie-primary transition-colors"
                          onClick={() => handleSort("lastUsed")}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3 w-3" /> Last Used{sortIndicator("lastUsed")}
                          </span>
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-[#7C8B9C] uppercase tracking-wide">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedGenies.map((genie) => {
                        const status = getGenieStatus(genie.lastUsedAt);
                        const badge = STATUS_BADGE[status];
                        const swatch =
                          genie.branding?.primaryColor ||
                          (genie.brand === "secondary" ? "#FF4DB8" : "#4E9CFF");
                        const avatarUrl = genie.branding?.customAvatarUrl;
                        return (
                          <TableRow key={genie.id} className="hover:bg-[#F9FAFC]/60 transition-colors">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    alt={genie.name}
                                    className="h-9 w-9 rounded-xl object-cover flex-shrink-0 shadow-sm border border-[#E5EAF0]"
                                  />
                                ) : (
                                  <div
                                    className="h-9 w-9 rounded-xl flex-shrink-0 shadow-sm flex items-center justify-center"
                                    style={{
                                      background: `linear-gradient(135deg, ${swatch} 0%, ${swatch}aa 100%)`,
                                    }}
                                  >
                                    <Bot className="h-4 w-4 text-white" />
                                  </div>
                                )}
                                <div className="font-medium text-sm text-[#1A1A1A]">{genie.name}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-semibold text-sm text-[#1A1A1A]">
                                {genie.conversationCount}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {genie.recentConversations > 0 ? (
                                <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-md bg-genie-secondary/10 text-genie-secondary font-semibold text-sm">
                                  {genie.recentConversations}
                                </span>
                              ) : (
                                <span className="text-gray-300 text-sm">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-[#7C8B9C] whitespace-nowrap">
                              {formatLastUsed(genie.lastUsedAt)}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium whitespace-nowrap ${badge.className}`}
                              >
                                <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} />
                                {badge.label}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserAnalyticsPage;
